// gridview — pure client-side filter + sort for an already-loaded result set.
//
// The Browser data grid filters/sorts SERVER-SIDE (WHERE/ORDER BY re-query).
// Query results are a static in-memory snapshot, so here we apply the same
// filter operators (mirroring FilterPopover.FILTER_OPS) and a multi-key sort
// over the loaded rows in JS. Semantics are "good-enough SQL-like" (the user is
// visibly filtering a loaded snapshot, not re-querying): NULLs never match a
// value comparison, equality/contains are case-insensitive, ordered comparisons
// are numeric for numeric columns (falling back to lexical), and NULLs sort
// last regardless of direction.
//
// Pure + dependency-light (only isNumericType) so it's unit-tested under bare
// node (gridview.test.mjs).
import { isNumericType, formatNumberWithSep } from './format.js';

// testFilter — does one cell pass one {op, value} filter? `numeric` = the
// column is a numeric type (drives ordered-comparison + equality coercion).
export function testFilter(cell, op, value, numeric) {
  const isNull = cell === null || cell === undefined;
  if (op === 'is_null')      return isNull;
  if (op === 'is_not_null')  return !isNull;
  if (op === 'is_empty')     return isNull || String(cell) === '';
  if (op === 'is_not_empty') return !isNull && String(cell) !== '';
  if (isNull) return false; // value comparisons never match NULL (SQL-like)
  const s = String(cell);
  if (op === 'like') return s.toLowerCase().includes(String(value).toLowerCase());
  if (op === '=')  return numeric ? Number(s) === Number(value) : s.toLowerCase() === String(value).toLowerCase();
  if (op === '<>') return numeric ? Number(s) !== Number(value) : s.toLowerCase() !== String(value).toLowerCase();
  // Ordered comparisons: numeric when the column is numeric AND both parse,
  // else lexical.
  let a = s, b = String(value);
  if (numeric) {
    const na = Number(s), nb = Number(value);
    if (Number.isFinite(na) && Number.isFinite(nb)) { a = na; b = nb; }
  }
  if (op === '<')  return a <  b;
  if (op === '<=') return a <= b;
  if (op === '>')  return a >  b;
  if (op === '>=') return a >= b;
  return true; // unknown op → don't filter out
}

// rowMatchesFilters — combine all filters with their per-pair AND/OR connectors,
// using SQL precedence (AND binds tighter than OR): the result is the OR of
// AND-groups. filters[0].connector is ignored.
export function rowMatchesFilters(row, filters, colIndex, numericSet) {
  if (!filters || filters.length === 0) return true;
  let result = null, group = null;
  for (let i = 0; i < filters.length; i++) {
    const f = filters[i];
    const ci = colIndex.get(f.column);
    const pass = ci == null ? true : testFilter(row[ci], f.op, f.value, numericSet.has(f.column));
    if (i === 0) { group = pass; continue; }
    if ((f.connector || 'AND') === 'OR') { result = result === null ? group : (result || group); group = pass; }
    else { group = group && pass; }
  }
  result = result === null ? group : (result || group);
  return !!result;
}

// applyFilterSort — return a filtered + sorted COPY of rows (or the original
// array unchanged when there's nothing to do, so callers can rely on referential
// stability to skip work). columns are [{name, type}]; sortKeys are
// [{column, dir:'asc'|'desc'}] applied in order (first = primary).
export function applyFilterSort(rows, columns, filters, sortKeys) {
  rows = rows || [];
  const cols = columns || [];
  const colIndex = new Map(cols.map((c, i) => [c.name, i]));
  const numericSet = new Set(cols.filter((c) => isNumericType(c.type)).map((c) => c.name));

  let out = rows;
  if (filters && filters.length) out = rows.filter((r) => rowMatchesFilters(r, filters, colIndex, numericSet));

  if (sortKeys && sortKeys.length) {
    out = out.slice().sort((ra, rb) => {
      for (const k of sortKeys) {
        const ci = colIndex.get(k.column);
        if (ci == null) continue;
        const av = ra[ci], bv = rb[ci];
        const an = av === null || av === undefined, bn = bv === null || bv === undefined;
        if (an || bn) { if (an && bn) continue; return an ? 1 : -1; } // NULLs last, dir-independent
        const cmp = compareCells(av, bv, numericSet.has(k.column));
        if (cmp !== 0) return k.dir === 'desc' ? -cmp : cmp;
      }
      return 0;
    });
  }
  return out;
}

// cycleSort — the shared sort-key cycle used by both the Browser data grid
// (server-side ORDER BY) and the query-result grid (client-side). Clicking a
// column: not sorted → ASC; ASC → DESC; DESC → removed. ctrlKey appends a NEW
// column as another key instead of replacing; on an already-sorted column the
// modifier is irrelevant (it only governs whether new columns join or replace).
// Returns the next sortKeys array (does not mutate the input).
export function cycleSort(sortKeys, colName, ctrlKey) {
  const cur = sortKeys || [];
  const idx = cur.findIndex((s) => s.column === colName);
  if (idx >= 0) {
    return cur[idx].dir === 'asc'
      ? cur.map((s, i) => (i === idx ? { column: colName, dir: 'desc' } : s))
      : cur.filter((_, i) => i !== idx);
  }
  return ctrlKey ? [...cur, { column: colName, dir: 'asc' }] : [{ column: colName, dir: 'asc' }];
}

// pageRange — the Browser data grid's pager label. `count` rows are loaded on
// 0-based `page` of `pageSize`; `estimate` is the server's row estimate (InnoDB
// TABLE_ROWS, easily off by tens of percent) or null when it doesn't apply
// (a filtered view). A short page is the last one, so its total is exact and
// beats the estimate; a full page shows the estimate only while it still lies
// ahead ("of ~2,669"), else just "of 1,000+". Returns { label, hasPrev, hasNext }.
export function pageRange(page, pageSize, count, estimate) {
  const fmt = formatNumberWithSep;
  const start = page * pageSize;
  const hasPrev = page > 0;
  const hasNext = count >= pageSize;
  if (count === 0) {
    return { label: hasPrev ? `no rows after ${fmt(start)}` : '0 rows', hasPrev, hasNext: false };
  }
  const last = start + count;
  if (!hasPrev && !hasNext) {
    return { label: `${fmt(count)} row${count === 1 ? '' : 's'}`, hasPrev, hasNext };
  }
  let of;
  if (!hasNext) of = fmt(last);
  else if (estimate != null && estimate > last) of = `~${fmt(estimate)}`;
  else of = `${fmt(last)}+`;
  return { label: `rows ${fmt(start + 1)}–${fmt(last)} of ${of}`, hasPrev, hasNext };
}

function compareCells(a, b, numeric) {
  if (numeric) {
    const na = Number(a), nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  }
  // Lexical, but numeric-aware ("2" < "10") and case-insensitive.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}
